import type { PropertyIn, PropertyPath } from '@/scopes/common/domain/types'
import { createStore, StoreApi } from 'zustand'
import { setIn } from '@/scopes/common/domain/functions/objectDeepOperations'
import { FilterSelection, FiltersResponse } from '@/scopes/prices/domain/filters/types'
import { PricesResponse } from '@/scopes/prices/domain/prices/types'
import { PromotedPrice } from '@/scopes/prices/domain/promoted-price/types'
import { RoomInfo, RoomSelectorResponse } from '@/scopes/prices/domain/rooms/types'
import {
  BusSelectorMappedResponse,
  RelatedInboundRoute,
  RouteInfo,
  type SelfDriveSelectorResponse,
  TransportSelectorMappedResponse,
} from '@/scopes/prices/domain/transports/types'
import { PriceTableAnalyticsWithBrand } from '@/scopes/prices/domain/types'
import { IsAccommodationAvailableRawResponseData } from '@/scopes/prices/infrastructure/availability/types'

export const emptyPricesResponse = {
  prices: [],
  pricesByDuration: {},
  pricesByDurationAndDate: {},
  year: '0',
  dates: [],
}

export const emptyRoomsResponse = {
  stock: {},
  roomGroups: [],
  specialAgeProfiles: {},
}

export const emptyTransportResponse = {
  transportType: '',
  outboundRoutes: [],
  inboundRoutes: [],
  tripSummary: [],
  paymentMethod: null,
}

export const emptyBusResponse = {
  transportType: '',
  outboundBuses: [],
  inboundBuses: [],
  routes: {},
  tripSummary: [],
  paymentMethod: null,
}

export const emptySelfDriveResponse = {
  transportType: '',
  paymentMethod: null,
}

export const emptyPromotedPriceResponse: PromotedPrice = {
  price: {
    value: 0,
    averagePrice: 0,
    totalPrice: 0,
    originalValue: 0,
    originalAveragePrice: 0,
    originalTotalPrice: 0,
    discountPercentage: 0,
    supportText: '',
  },
  accommodationId: 0,
  legacyDepartureDate: {
    date: '',
    formatted: '',
  },
  departureDate: new Date(0),
  duration: 0,
  services: [],
  filters: [],
  departureAirport: null,
  transportType: '',
  mealplan: '',
  campaignImage: undefined,
  campaignId: undefined,
  acm: {
    transportType: '',
    departureDateRaw: '',
    durationRaw: 0,
    birthdates: [],
    mealplanCode: '',
    acmUrl: '',

  },
  paymentMethod: null,
  campaignCountDown: undefined,
}

export const emptyIsAccommodationAvailableResponse = {
  isAccommodationAvailable: false,
  isFlightOnly: false,
  transportTypes: [],
}

export interface PricesState {
  filterSelection: FilterSelection
  accoId: string
  contextItemId: string
  bookingGateId: string
  getFilters: {
    loading: boolean
    error: boolean
    isEmpty: boolean
  }
  getPrices: {
    loading: boolean
    error: boolean
  }
  getRooms: {
    loading: boolean
    error: boolean
  }
  getTransport: {
    loading: boolean
    error: boolean
  }
  getPromotedPrice: {
    loading: boolean
    error: boolean
    success: boolean
  }
  isAccommodationAvailable: {
    loading: boolean
    error: boolean
    success: boolean
  }
  filterOptions: Partial<FiltersResponse>
  pricesResponse: PricesResponse
  selectedPriceId: string | null
  roomsResponse: RoomSelectorResponse
  selectedRooms: Record<string, string | null> | null
  selectedRoomInfos: RoomInfo[]
  flightSelectorResponse: TransportSelectorMappedResponse
  busSelectorResponse: BusSelectorMappedResponse
  selectedOutboundRouteId: string | null
  selectedInboundRouteId: string | null
  selectedOutboundBusId: string | null
  selectedInboundBusId: string | null
  outboundRoutes: RouteInfo[]
  inboundRoutes: RelatedInboundRoute[]
  selfDriveResponse: SelfDriveSelectorResponse
  createBookingDraft: {
    loading: boolean
    error: boolean
    success: boolean
  }
  analytics: PriceTableAnalyticsWithBrand | null
  promotedPriceResponse: PromotedPrice | null
  isAccommodationAvailableResponse: IsAccommodationAvailableRawResponseData
  promotedPriceId: string
  refreshPromotedPrice: boolean
  requestedDepartureDateOnLoad: string | null
}

type PricesStatePath = PropertyPath<PricesState>

export interface PricesStoreActions {
  setIn: (path: PricesStatePath, value: PropertyIn<PricesState, typeof path>) => void
}

const initialState = {
  filterSelection: {},
  accoId: '',
  contextItemId: '',
  bookingGateId: '',
  getFilters: {
    loading: true,
    error: false,
    isEmpty: false,
  },
  getPrices: {
    loading: false,
    error: false,
  },
  getRooms: {
    loading: false,
    error: false,
  },
  getTransport: {
    loading: false,
    error: false,
  },
  getPromotedPrice: {
    loading: false,
    error: false,
    success: false,
  },
  isAccommodationAvailable: {
    loading: true,
    error: false,
    success: false,
  },
  filterOptions: {},
  pricesResponse: emptyPricesResponse,
  selectedPriceId: null,
  roomsResponse: emptyRoomsResponse,
  selectedRooms: null,
  selectedRoomInfos: [],
  flightSelectorResponse: emptyTransportResponse,
  busSelectorResponse: emptyBusResponse,
  selectedOutboundRouteId: null,
  selectedInboundRouteId: null,
  selectedOutboundBusId: null,
  selectedInboundBusId: null,
  outboundRoutes: [],
  inboundRoutes: [],
  selfDriveResponse: emptySelfDriveResponse,
  createBookingDraft: {
    loading: false,
    error: false,
    success: false,
  },
  analytics: null,
  promotedPriceResponse: null,
  isAccommodationAvailableResponse: emptyIsAccommodationAvailableResponse,
  promotedPriceId: '',
  refreshPromotedPrice: false,
  requestedDepartureDateOnLoad: null,
}

export type PricesStore = PricesState & PricesStoreActions
export type PricesStoreApi = Pick<StoreApi<PricesStore>, 'getState'>

export function createPricesStore(initState: PricesState): StoreApi<PricesStore> {
  return createStore<PricesStore>(set => ({
    ...initState,
    setIn: (path, value) => {
      set(state => setIn(state, path, value) as PricesState)
    },
  }))
}

export const newPricesStore = createPricesStore(initialState)
